<template>
    <div class="page_content online-defense-detail" v-loading="loading">
        <div class="page_heading">
            <h1 class="head_title">在线答辩详情</h1>
            <el-button icon="el-icon-arrow-left" @click="$router.push({ name: 'OnlineDefenseList' })">返回列表</el-button>
        </div>

        <el-alert v-if="loadError" title="在线答辩详情加载失败，请重试" type="error" show-icon :closable="false">
            <el-button type="text" @click="loadPage">重新加载</el-button>
        </el-alert>

        <template v-if="detail && loaded">
            <el-card shadow="never" class="detail-card">
                <div slot="header" class="card-title">{{ detail.onlineDefenseSessionTitle }}</div>
                <el-descriptions :column="2" border>
                    <el-descriptions-item label="答辩分组">{{ detail.onlineDefenseGroupName || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="活动状态">{{ groupStatusText(detail.onlineDefenseGroupStatus) }}</el-descriptions-item>
                    <el-descriptions-item label="开始时间">{{ detail.groupStartTime || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="结束时间">{{ detail.groupEndTime || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="资料截止时间">{{ detail.materialDeadline || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="允许进入时间">{{ entryWindowText }}</el-descriptions-item>
                    <el-descriptions-item label="活动说明" :span="2">
                        <span class="description">{{ detail.onlineDefenseSessionDescription || '暂无说明' }}</span>
                    </el-descriptions-item>
                    <el-descriptions-item v-if="attachmentList.length" label="活动附件" :span="2">
                        <div class="session-attachment-list">
                            <el-button
                                v-for="attachment in attachmentList"
                                :key="attachment.onlineDefenseSessionAttachmentId || attachment.attachmentUrl"
                                type="text"
                                icon="el-icon-document"
                                @click="downloadAttachment(attachment)">
                                {{ attachment.attachmentName || '下载附件' }}
                            </el-button>
                        </div>
                    </el-descriptions-item>
                </el-descriptions>
                <div class="action-bar">
                    <el-button
                        v-if="capabilities.deviceCheckEnabled"
                        icon="el-icon-monitor"
                        :loading="deviceLoading"
                        @click="openDeviceTest">
                        设备检测
                    </el-button>
                    <el-button type="primary" icon="el-icon-video-camera" :loading="entryLoading" @click="enterDefense">
                        进入答辩
                    </el-button>
                </div>
            </el-card>

            <el-card shadow="never" class="detail-card">
                <div slot="header" class="material-header">
                    <span class="card-title">我的答辩资料</span>
                    <el-button
                        v-if="canEditMaterials"
                        type="primary"
                        size="small"
                        icon="el-icon-upload"
                        @click="uploadDialogVisible = true">
                        上传资料
                    </el-button>
                </div>
                <el-alert
                    v-if="!canEditMaterials"
                    title="资料提交时间已结束或当前学校未开启资料提交"
                    type="info"
                    :closable="false"
                    show-icon>
                </el-alert>
                <el-table :data="detail.materialList || []" border style="width: 100%; margin-top: 15px">
                    <el-table-column prop="materialName" label="文件名称" min-width="260">
                        <template slot-scope="scope">
                            <el-link type="primary" :href="$isHttp(scope.row.materialUrl)" target="_blank">{{ scope.row.materialName }}</el-link>
                        </template>
                    </el-table-column>
                    <el-table-column label="大小" width="110" align="right">
                        <template slot-scope="scope">{{ formatSize(scope.row.materialSize) }}</template>
                    </el-table-column>
                    <el-table-column prop="materialVersion" label="版本" width="80" align="center"></el-table-column>
                    <el-table-column label="同步状态" width="110" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="materialStatusType(scope.row.materialSyncStatus)" size="small">
                                {{ materialStatusText(scope.row.materialSyncStatus) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column v-if="canEditMaterials" label="操作" width="90" align="center">
                        <template slot-scope="scope">
                            <el-button type="text" class="danger-button" @click="deleteMaterial(scope.row)">删除</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <el-empty v-if="(detail.materialList || []).length === 0" description="暂未上传答辩资料" :image-size="80"></el-empty>
            </el-card>
        </template>

        <el-dialog title="上传答辩资料" :visible.sync="uploadDialogVisible" width="560px" @close="resetUploadFiles">
            <el-upload
                ref="materialUpload"
                class="upload-demo"
                action="#"
                :auto-upload="false"
                :file-list="uploadFileList"
                :on-change="handleFileChange"
                :on-remove="handleFileRemove"
                :before-upload="beforeUpload"
                :accept="acceptedExtensions"
                multiple>
                <el-button size="small" type="primary">选择文件</el-button>
                <div slot="tip" class="el-upload__tip material-format-tip">
                    <div>文档和图片：DOC、DOCX、PPT、PPTX、PDF、JPG、JPEG、PNG、GIF</div>
                    <div>音频：MP3、WAV、WMA、AAC、FLAC、M4A、OGA、OPUS</div>
                    <div>视频：MP4、3GP、MPG、MPEG、3G2、AVI、FLV、WMV、H264、M4V、MJ2、MOV、OGG、OGV、RM、RMVB、QT、WEBM</div>
                    <div>音频和视频单个文件最大 50MB。</div>
                </div>
            </el-upload>
            <span slot="footer" class="dialog-footer">
                <el-button @click="uploadDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="uploading" @click="submitMaterials">提 交</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
const audioVideoExtensions = ['mp3', 'wav', 'wma', 'aac', 'flac', 'm4a', 'oga', 'opus', 'mp4', '3gp', 'mpg', 'mpeg', '3g2', 'avi', 'flv', 'wmv', 'h264', 'm4v', 'mj2', 'mov', 'ogg', 'ogv', 'rm', 'rmvb', 'qt', 'webm'];
const supportedExtensions = ['doc', 'docx', 'ppt', 'pptx', 'pdf', 'jpg', 'jpeg', 'png', 'gif'].concat(audioVideoExtensions);

export default {
    name: 'OnlineDefenseDetail',
    data() {
        return {
            loading: false,
            loaded: false,
            detail: null,
            capabilities: {},
            uploadDialogVisible: false,
            uploadFileList: [],
            uploading: false,
            deviceLoading: false,
            entryLoading: false,
            loadError: false,
            acceptedExtensions: supportedExtensions.map((extension) => '.' + extension).join(',')
        };
    },
    computed: {
        onlineDefenseGroupId() {
            return this.$route.query.onlineDefenseGroupId;
        },
        deadlinePassed() {
            if (!this.detail || !this.detail.materialDeadline) return false;
            return new Date(String(this.detail.materialDeadline).replace(/-/g, '/')).getTime() < Date.now();
        },
        canEditMaterials() {
            return this.capabilities.materialEnabled && !this.deadlinePassed;
        },
        entryWindowText() {
            if (!this.detail || !this.detail.groupStartTime || !this.detail.groupEndTime) return '-';
            const before = this.detail.preEnterMinutes || 0;
            const after = this.detail.endDelayMinutes || 0;
            return '开始前 ' + before + ' 分钟至结束后 ' + after + ' 分钟';
        },
        attachmentList() {
            if (!this.detail) return [];
            if (this.detail.onlineDefenseSessionAttachmentList && this.detail.onlineDefenseSessionAttachmentList.length) {
                return this.detail.onlineDefenseSessionAttachmentList;
            }
            return this.detail.onlineDefenseSessionAttachmentUrl ? [{
                attachmentName: this.detail.onlineDefenseSessionAttachmentUrl.split('/').pop(),
                attachmentUrl: this.detail.onlineDefenseSessionAttachmentUrl
            }] : [];
        }
    },
    created() {
        this.loadPage();
    },
    methods: {
        loadPage() {
            if (!this.onlineDefenseGroupId) {
                this.$message.error('答辩分组参数错误');
                this.$router.replace({ name: 'OnlineDefenseList' });
                return;
            }
            this.loading = true;
            this.loadError = false;
            this.$store.dispatch('onlineDefense/getCapabilities').then((capabilityRet) => {
                if (capabilityRet.code != 0 && capabilityRet.code != '0') {
                    this.loadError = true;
                    this.$message.error(capabilityRet.msg || '获取在线答辩能力失败');
                    return;
                }
                this.capabilities = capabilityRet.data || {};
                if (!this.capabilities.enabled) {
                    this.$message.warning('在线答辩功能暂未开启');
                    this.$router.replace({ name: 'OnlineDefenseList' });
                    return;
                }
                return this.refreshDetail();
            }).catch(() => {
                this.loadError = true;
                this.$message.error('获取在线答辩详情失败，请稍后重试');
            }).finally(() => {
                this.loading = false;
                this.loaded = true;
            });
        },
        refreshDetail() {
            return this.$store.dispatch('onlineDefense/getInfo', {
                onlineDefenseGroupId: this.onlineDefenseGroupId
            }).then((ret) => {
                if (ret.code == 0 || ret.code == '0') {
                    this.detail = ret.data;
                } else {
                    this.loadError = true;
                    this.$message.error(ret.msg || '获取在线答辩详情失败');
                }
                return ret;
            });
        },
        downloadAttachment(attachment) {
            this.$downLoadFile(attachment.attachmentUrl, attachment.attachmentName || '在线答辩附件');
        },
        openDeviceTest() {
            const deviceWindow = window.open('', '_blank');
            if (!deviceWindow) {
                this.$message.warning('浏览器阻止了新窗口，请允许弹窗后重试');
                return;
            }
            deviceWindow.opener = null;
            this.deviceLoading = true;
            this.$store.dispatch('onlineDefense/getDeviceTest', {
                onlineDefenseGroupId: this.onlineDefenseGroupId
            }).then((ret) => {
                if ((ret.code == 0 || ret.code == '0') && ret.data && ret.data.deviceTestUrl) {
                    deviceWindow.location.replace(ret.data.deviceTestUrl);
                } else {
                    deviceWindow.close();
                    this.$message.error(ret.msg || '设备检测地址暂不可用');
                }
            }).catch((error) => {
                deviceWindow.close();
                this.$message.error(this.getErrorMessage(error, '获取设备检测地址失败，请稍后重试'));
            }).finally(() => {
                this.deviceLoading = false;
            });
        },
        enterDefense() {
            const entryWindow = window.open('', '_blank');
            if (!entryWindow) {
                this.$message.warning('浏览器阻止了新窗口，请允许弹窗后重试');
                return;
            }
            entryWindow.opener = null;
            this.entryLoading = true;
            this.$store.dispatch('onlineDefense/getEntryUrl', {
                onlineDefenseGroupId: this.onlineDefenseGroupId
            }).then((ret) => {
                if ((ret.code == 0 || ret.code == '0') && ret.data && ret.data.entryUrl) {
                    entryWindow.location.replace(ret.data.entryUrl);
                } else {
                    entryWindow.close();
                    this.$message.error(ret.msg || '在线答辩入口暂未准备完成');
                }
            }).catch((error) => {
                entryWindow.close();
                this.$message.error(this.getErrorMessage(error, '获取在线答辩入口失败，请稍后重试'));
            }).finally(() => {
                this.entryLoading = false;
            });
        },
        handleFileChange(file, fileList) {
            this.uploadFileList = fileList;
        },
        handleFileRemove(file, fileList) {
            this.uploadFileList = fileList;
        },
        resetUploadFiles() {
            this.uploadFileList = [];
            if (this.$refs.materialUpload) this.$refs.materialUpload.clearFiles();
        },
        beforeUpload(file) {
            const name = file.name || '';
            const extension = name.indexOf('.') >= 0 ? name.split('.').pop().toLowerCase() : '';
            if (supportedExtensions.indexOf(extension) === -1) {
                this.$message.error('该资料格式不支持');
                return false;
            }
            if (audioVideoExtensions.indexOf(extension) >= 0 && file.size > 50 * 1024 * 1024) {
                this.$message.error('音频和视频单个文件不能超过 50MB');
                return false;
            }
            return true;
        },
        submitMaterials() {
            const files = this.uploadFileList.map((item) => item.raw).filter((file) => !!file);
            if (files.length === 0) {
                this.$message.warning('请先选择资料');
                return;
            }
            for (let index = 0; index < files.length; index++) {
                if (!this.beforeUpload(files[index])) return;
            }
            const formData = new FormData();
            formData.append('onlineDefenseGroupId', this.onlineDefenseGroupId);
            files.forEach((file) => formData.append('file', file, file.name));
            this.uploading = true;
            this.$store.dispatch('onlineDefense/uploadMaterials', formData).then((ret) => {
                if (ret.code == 0 || ret.code == '0') {
                    this.$message.success('答辩资料已提交');
                    this.uploadDialogVisible = false;
                    return this.refreshDetail();
                }
                this.$message.error(ret.msg || '答辩资料提交失败');
            }).catch((error) => {
                this.$message.error(this.getErrorMessage(error, '答辩资料提交失败，请稍后重试'));
            }).finally(() => {
                this.uploading = false;
            });
        },
        deleteMaterial(material) {
            this.$confirm('确定删除该答辩资料吗？', '提示', { type: 'warning' }).then(() => {
                return this.$store.dispatch('onlineDefense/deleteMaterial', {
                    onlineDefenseMaterialId: material.onlineDefenseMaterialId
                });
            }).then((ret) => {
                if (ret.code == 0 || ret.code == '0') {
                    this.$message.success('答辩资料已删除');
                    return this.refreshDetail();
                }
                this.$message.error(ret.msg || '删除答辩资料失败');
            }).catch((error) => {
                if (error !== 'cancel') this.$message.error(this.getErrorMessage(error, '删除答辩资料失败，请稍后重试'));
            });
        },
        getErrorMessage(error, fallback) {
            const response = error && error.response;
            let data = response && response.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (ignore) {
                    return data || fallback;
                }
            }
            return data && (data.msg || data.message) ? (data.msg || data.message) : fallback;
        },
        groupStatusText(status) {
            return {
                1: '待创建', 2: '创建中', 3: '已就绪', 4: '进行中', 5: '归档中', 6: '已归档', 7: '失败', 8: '已取消'
            }[status] || '未知';
        },
        materialStatusText(status) {
            return { 1: '同步中', 2: '同步中', 3: '已同步', 4: '同步失败' }[status] || '未知';
        },
        materialStatusType(status) {
            if (status == 3) return 'success';
            if (status == 4) return 'danger';
            return 'warning';
        },
        formatSize(size) {
            if (!size) return '0 B';
            if (size < 1024) return size + ' B';
            if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
            return (size / 1024 / 1024).toFixed(1) + ' MB';
        }
    }
};
</script>

<style scoped lang="less">
.online-defense-detail {
    min-height: calc(100vh - 100px);
}
.page_heading,
.material-header,
.action-bar {
    display: flex;
    align-items: center;
}
.page_heading,
.material-header {
    justify-content: space-between;
}
.page_heading .head_title {
    margin-bottom: 20px;
}
.detail-card {
    margin-bottom: 16px;
}
.card-title {
    font-size: 17px;
    font-weight: 600;
}
.description {
    white-space: pre-line;
}
.action-bar {
    justify-content: flex-end;
    margin-top: 18px;
    gap: 10px;
}
.danger-button {
    color: #f56c6c;
}
.material-format-tip {
    line-height: 20px;
    white-space: normal;
}
</style>



// WEBPACK FOOTER //
// src/pages/onlineDefense/OnlineDefenseDetail.vue